// Gestão em Lote · Bulk Management page (MVP)
// Fluxo: Painel Qualidade → Filtros → Resultado → Seleção → Alterações → Preview → Confirmação → Execução
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wrench, Filter, Plus, X, Play, AlertTriangle, ClipboardCheck,
  ChevronDown, ChevronRight, Trash2, Eraser, FileEdit, ShieldAlert, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { FilterGroup } from "./BulkFilterBuilder";

const NEEDS_NONE  = ["empty","not_empty","last_30_days"];

export default function BulkManagementPage() {
  const [fields, setFields] = useState([]);
  const [quality, setQuality] = useState(null);
  const [filter, setFilter] = useState({ op: "AND", children: [] });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllInFilter, setSelectAllInFilter] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [applying, setApplying] = useState(false);
  const [impactAck, setImpactAck] = useState(false);  // dupla confirmação p/ impacto alto
  const [lastBatch, setLastBatch] = useState(null);   // guarda batch_id do apply p/ CSV
  const [presets, setPresets] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetDesc, setPresetDesc] = useState("");
  const [presetShared, setPresetShared] = useState(false);
  const [presetCron, setPresetCron] = useState("");

  useEffect(() => {
    api.get("/bulk/fields").then(({data}) => setFields(data.fields || [])).catch(() => toast.error("Sem permissão para Gestão em Lote."));
    api.get("/bulk/quality-summary").then(({data}) => setQuality(data)).catch(() => {});
    api.get("/bulk/presets").then(({data}) => setPresets(data.items || [])).catch(() => {});
  }, []);

  // Fontes de opções (para dropdowns em fields com options_source: companies/sectors)
  const [optionsBank, setOptionsBank] = useState({ companies: [], sectors: [] });
  useEffect(() => {
    (async () => {
      try {
        const [co, sc] = await Promise.all([
          api.get("/companies"),
          api.get("/sectors"),
        ]);
        setOptionsBank({
          companies: (co.data || []).map(c => ({ id: c.id, label: c.nome_fantasia || c.razao_social || c.id })),
          sectors: (sc.data || []).map(s => ({ id: s.id, label: s.name || s.id, company_id: s.company_id })),
        });
      } catch { /* silêncio: dropdowns cairão para fallback */ }
    })();
  }, []);

  const fieldsByKey = useMemo(() => Object.fromEntries(fields.map(f => [f.key, f])), [fields]);
  const editableFields = fields.filter(f => f.editable_by_user);

  const runSearch = async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await api.post("/bulk/search", { filter, page: p, page_size: 50 });
      setResults(data);
      setPage(p);
      setSelectedIds(new Set());
      setSelectAllInFilter(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const applyQualityFilter = (fieldKey) => {
    const newFilter = { op: "AND", children: [{ field: fieldKey, op: "empty" }] };
    setFilter(newFilter);
    setTimeout(() => runSearchWith(newFilter), 50);
  };
  const runSearchWith = async (f) => {
    setLoading(true);
    try {
      const { data } = await api.post("/bulk/search", { filter: f, page: 1, page_size: 50 });
      setResults(data); setPage(1); setSelectedIds(new Set()); setSelectAllInFilter(false);
    } finally { setLoading(false); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
    setSelectAllInFilter(false);
  };

  const effectiveTargetsCount = selectAllInFilter ? (results?.total || 0) : selectedIds.size;

  const doPreview = async () => {
    if (!updates.length) { toast.error("Marque pelo menos um campo para alterar."); return; }
    if (effectiveTargetsCount === 0) { toast.error("Selecione colaboradores."); return; }
    setPreviewing(true); setPreview(null); setImpactAck(false);
    try {
      const body = {
        updates: updates.map(u => ({ field: u.field, mode: u.mode, value: u.value })),
      };
      if (selectAllInFilter) body.filter = filter;
      else body.target_ids = Array.from(selectedIds);
      const { data } = await api.post("/bulk/preview", body);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPreviewing(false); }
  };

  // Baixa o CSV do que SERIA alterado (dry-run) — para aprovação prévia
  const downloadDryRunCsv = async () => {
    try {
      const body = {
        updates: updates.map(u => ({ field: u.field, mode: u.mode, value: u.value })),
      };
      if (selectAllInFilter) body.filter = filter;
      else body.target_ids = Array.from(selectedIds);
      const resp = await api.post("/bulk/preview/export.csv", body, { responseType: "blob" });
      _downloadBlob(resp.data, `bulk-dry-run-${Date.now()}.csv`);
      toast.success("CSV de simulação baixado.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Baixa o CSV do último batch aplicado
  const downloadBatchCsv = async (batchId) => {
    try {
      const resp = await api.get(`/bulk/operations/${batchId}/export.csv`, { responseType: "blob" });
      _downloadBlob(resp.data, `bulk-${batchId.slice(0,8)}.csv`);
      toast.success("CSV do lote baixado.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const _downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  // Presets
  const savePreset = async () => {
    const name = (presetName || "").trim();
    if (!name) { toast.error("Dê um nome ao preset."); return; }
    if (!updates.length) { toast.error("Configure ao menos um update."); return; }
    try {
      const body = {
        name, description: presetDesc,
        filter: filter && filter.children?.length ? filter : null,
        updates: updates.map(u => ({ field: u.field, mode: u.mode, value: u.value })),
        shared: presetShared,
        schedule_cron: presetCron.trim() || null,
      };
      const { data } = await api.post("/bulk/presets", body);
      setPresets((prev) => [data, ...prev]);
      setPresetName(""); setPresetDesc(""); setPresetShared(false); setPresetCron("");
      toast.success(`Preset "${name}" salvo.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const patchPreset = async (preset, patch) => {
    try {
      const { data } = await api.patch(`/bulk/presets/${preset.id}`, patch);
      setPresets((prev) => prev.map(x => x.id === preset.id ? data : x));
      toast.success("Preset atualizado.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runPresetNow = async (preset) => {
    if (!window.confirm(`Executar agora o preset "${preset.name}"? Isso rodará o filtro sobre os dados atuais.`)) return;
    try {
      const { data } = await api.post(`/bulk/presets/${preset.id}/run`);
      if (data.requires_second_approval) {
        toast.info(`Pedido de aprovação criado (${data.targets} colaboradores). Requer aprovação de outro Master.`);
      } else {
        setLastBatch({ batch_id: data.batch_id, targets: data.targets });
        try { window.dispatchEvent(new CustomEvent("bulk-employees-updated", { detail: data })); } catch {}
        toast.success(`Preset executado. Alvos: ${data.targets}. Batch: ${data.batch_id.slice(0, 8)}…`);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadPreset = (p) => {
    setFilter(p.filter || { op: "AND", children: [] });
    setUpdates(p.updates || []);
    setSelectedIds(new Set());
    setSelectAllInFilter(false);
    setPreview(null); setImpactAck(false);
    toast.success(`Preset "${p.name}" carregado. Rode Buscar e Pré-visualizar para aplicar.`);
  };

  const deletePreset = async (p) => {
    if (!window.confirm(`Excluir preset "${p.name}"?`)) return;
    try {
      await api.delete(`/bulk/presets/${p.id}`);
      setPresets((prev) => prev.filter(x => x.id !== p.id));
      toast.success("Preset excluído.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doApply = async () => {
    setApplying(true);
    try {
      const body = {
        updates: updates.map(u => ({ field: u.field, mode: u.mode, value: u.value })),
        confirmation_text: confirmText,
      };
      if (selectAllInFilter) body.filter = filter;
      else body.target_ids = Array.from(selectedIds);
      const { data } = await api.post("/bulk/apply", body);
      if (data.requires_second_approval) {
        toast.info(data.message || `Pedido pendente criado. Requer aprovação de outro Master.`, { duration: 8000 });
        setPreview(null); setConfirmText(""); setImpactAck(false);
        return;
      }
      // Aviso global — páginas de Colaboradores, Dashboard, Setores reagem para invalidar cache
      try {
        window.dispatchEvent(new CustomEvent("bulk-employees-updated", {
          detail: { batch_id: data.batch_id, updates: body.updates, targets: data.targets },
        }));
      } catch { /* ignore */ }
      setLastBatch({ batch_id: data.batch_id, targets: data.targets });
      toast.success(`Batch ${data.batch_id.slice(0,8)}… concluído. Alvos: ${data.targets}. Alterações confirmadas no banco.`);
      setPreview(null); setConfirmText(""); setUpdates([]); setImpactAck(false);
      runSearch(page);
      api.get("/bulk/quality-summary").then(({data}) => setQuality(data));
    } catch (e) {
      const d = e.response?.data?.detail;
      let msg;
      if (typeof d === "object") {
        if (d.hint) {
          msg = `${d.message}${d.invalid_value ? ` (valor recebido: "${d.invalid_value}")` : ""} — ${d.hint}`;
        } else if (d.expected_pattern) {
          msg = `${d.message} — esperado: "${d.expected_pattern}"`;
        } else if (d.verification) {
          msg = d.message;
        } else {
          msg = d.message || formatApiError(d);
        }
      } else {
        msg = formatApiError(d);
      }
      toast.error(msg);
    } finally { setApplying(false); }
  };

  const confirmMatches = preview && confirmText.trim().toUpperCase() === ("CONFIRMAR ALTERAR " + preview.found).toUpperCase();

  return (
    <div className="space-y-4 pb-8" data-testid="bulk-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-[#065F46]" />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8A6B2B] font-semibold">Administração</div>
            <h1 className="font-display text-[20px] font-semibold text-slate-900 leading-tight">Gestão em Lote · Saneamento Cadastral</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild className="h-9 border-slate-300" data-testid="btn-open-approvals">
            <Link to="/gestao-em-lote/aprovacoes">
              <ShieldAlert className="h-4 w-4 mr-1.5" /> Aprovações
            </Link>
          </Button>
          <Button variant="outline" asChild className="h-9 border-slate-300" data-testid="btn-open-history">
            <Link to="/gestao-em-lote/historico">
              <ClipboardList className="h-4 w-4 mr-1.5" /> Histórico
            </Link>
          </Button>
        </div>
      </header>

      {/* Último batch aplicado — download de CSV */}
      {lastBatch && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2 flex items-center justify-between gap-2"
             data-testid="last-batch-bar">
          <div className="text-[12px] text-emerald-900">
            Último lote aplicado: <b className="font-mono">{lastBatch.batch_id.slice(0, 8)}…</b> · {lastBatch.targets} colaboradores.
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" onClick={() => downloadBatchCsv(lastBatch.batch_id)}
                    className="h-7 text-[11px]" data-testid="btn-last-batch-csv">
              <FileEdit className="h-3 w-3 mr-1" /> Baixar CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setLastBatch(null)}
                    className="h-7 text-[11px] text-slate-600">
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Presets — Reagendar operações */}
      <div className="rounded-md border border-slate-200 bg-white" data-testid="presets-section">
        <button className="w-full flex items-center justify-between px-3 py-2 text-[11.5px] font-medium text-slate-800"
                onClick={() => setShowPresets((v) => !v)} data-testid="btn-toggle-presets">
          <span className="flex items-center gap-1.5">
            <ClipboardCheck className="h-3.5 w-3.5 text-[#065F46]" />
            Presets salvos ({presets.length})
          </span>
          {showPresets ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        {showPresets && (
          <div className="border-t border-slate-200 p-3 space-y-2">
            <div className="text-[10.5px] text-slate-500 leading-tight">
              Salve um filtro + updates como preset e reaplique com um clique em datas futuras.
              O preset não persiste os IDs — ele reexecuta o filtro sobre os dados atuais na hora do uso.
            </div>
            {presets.length > 0 && (
              <div className="rounded border border-slate-200 divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {presets.map((p) => (
                  <PresetRow key={p.id} preset={p}
                             onLoad={() => loadPreset(p)}
                             onDelete={() => deletePreset(p)}
                             onRunNow={() => runPresetNow(p)}
                             onPatch={(patch) => patchPreset(p, patch)} />
                ))}
              </div>
            )}
            {updates.length > 0 && (
              <div className="flex flex-wrap gap-1.5 items-end pt-1 border-t border-slate-100">
                <div className="flex flex-col gap-0.5">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Nome</label>
                  <Input value={presetName} onChange={(e) => setPresetName(e.target.value)}
                         placeholder="Ex.: Padronizar CC Administrativo"
                         className="h-7 text-[11px] w-[240px]" data-testid="preset-name" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Descrição</label>
                  <Input value={presetDesc} onChange={(e) => setPresetDesc(e.target.value)}
                         placeholder="Descrição (opcional)"
                         className="h-7 text-[11px] w-[240px]" data-testid="preset-desc" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Cron (opcional, UTC)</label>
                  <Input value={presetCron} onChange={(e) => setPresetCron(e.target.value)}
                         placeholder="Ex.: 0 6 1 * *  (dia 1 às 06h UTC)"
                         className="h-7 text-[11px] w-[220px] font-mono" data-testid="preset-cron" />
                </div>
                <label className="flex items-center gap-1 text-[11px] text-slate-700 pb-1 cursor-pointer">
                  <input type="checkbox" checked={presetShared} onChange={(e) => setPresetShared(e.target.checked)}
                         data-testid="preset-shared" />
                  Compartilhar com o time
                </label>
                <Button size="sm" onClick={savePreset} className="h-7 text-[11px] bg-slate-800 hover:bg-slate-900"
                        data-testid="btn-save-preset">
                  <Plus className="h-3 w-3 mr-1" /> Salvar preset
                </Button>
              </div>
            )}
            {updates.length === 0 && (
              <div className="text-[10.5px] text-slate-400 italic">
                Configure um filtro e ao menos um update para poder salvar como preset.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Painel de Qualidade */}
      {quality && (
        <section className="rounded-xl border border-slate-200 bg-white p-3" data-testid="quality-panel">
          <div className="flex items-center gap-1.5 mb-2">
            <ClipboardCheck className="h-3.5 w-3.5 text-[#065F46]" />
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-700">Painel de Qualidade Cadastral</h2>
            <span className="text-[10.5px] text-slate-400 ml-auto">{quality.total.toLocaleString("pt-BR")} colaboradores analisados</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {quality.items.map(i => (
              <button key={i.field}
                      onClick={() => applyQualityFilter(i.field)}
                      className="rounded-lg border border-rose-200 bg-rose-50/60 hover:bg-rose-100 transition-colors p-2 text-left"
                      data-testid={`quality-${i.field}`}>
                <div className="font-display text-[20px] font-bold text-rose-700 tabular-nums leading-none">{i.count.toLocaleString("pt-BR")}</div>
                <div className="text-[10.5px] text-rose-800/90 mt-1 leading-tight">{i.label}</div>
              </button>
            ))}
            {quality.items.length === 0 && (
              <div className="col-span-full text-[12px] text-emerald-700 py-4 text-center">✓ Base cadastral íntegra em todos os campos monitorados.</div>
            )}
          </div>
        </section>
      )}

      {/* Filter builder */}
      <section className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Filter className="h-3.5 w-3.5 text-slate-600" />
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-700">Filtros</h2>
        </div>
        <FilterGroup node={filter} onChange={setFilter} fields={fields} depth={0} />
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" className="bg-[#065F46] hover:bg-[#054d38]" onClick={() => runSearch(1)} data-testid="btn-search">
            <Play className="h-3.5 w-3.5 mr-1.5" /> Buscar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setFilter({op:"AND",children:[]}); setResults(null); }}>Limpar filtros</Button>
        </div>
      </section>

      {/* Results */}
      {(loading || results) && (
        <section className="rounded-xl border border-slate-200 bg-white p-3" data-testid="results">
          {loading ? <Skeleton className="h-32" /> : (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[13px] font-semibold text-slate-800">
                  {results.total.toLocaleString("pt-BR")} colaboradores encontrados
                </div>
                <div className="flex items-center gap-2 text-[11.5px]">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox checked={selectAllInFilter}
                              onCheckedChange={(v) => { setSelectAllInFilter(!!v); setSelectedIds(new Set()); }}
                              data-testid="chk-select-all-filter" />
                    Selecionar TODOS os {results.total.toLocaleString("pt-BR")} resultados
                  </label>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11.5px]">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr><th className="text-left py-1 pr-2"></th>
                        <th className="text-left py-1 pr-2">Matrícula</th>
                        <th className="text-left py-1 pr-2">Nome</th>
                        <th className="text-left py-1 pr-2">Cargo</th>
                        <th className="text-left py-1 pr-2">Setor</th>
                        <th className="text-left py-1 pr-2">Turno</th>
                        <th className="text-left py-1 pr-2">Situação</th></tr>
                  </thead>
                  <tbody>
                    {results.items.map(r => {
                      const checked = selectAllInFilter || selectedIds.has(r.id);
                      return (
                        <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-1 pr-2">
                            <Checkbox checked={checked} disabled={selectAllInFilter}
                                      onCheckedChange={() => toggleSelect(r.id)}
                                      data-testid={`row-check-${r.id}`} />
                          </td>
                          <td className="py-1 pr-2 font-mono">{r.matricula || <span className="text-rose-500 italic">—</span>}</td>
                          <td className="py-1 pr-2">{r.nome}</td>
                          <td className="py-1 pr-2">{r.cargo || <span className="text-slate-400 italic">—</span>}</td>
                          <td className="py-1 pr-2 font-mono text-[10.5px]">{r.sector_id ? r.sector_id.slice(0,8) : <span className="text-slate-400 italic">—</span>}</td>
                          <td className="py-1 pr-2">{r.turno || <span className="text-slate-400 italic">—</span>}</td>
                          <td className="py-1 pr-2">{r.status || <span className="text-slate-400 italic">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
                <span>Página {page} · {results.items.length} de {results.total}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => runSearch(page-1)}>◀ Ant.</Button>
                  <Button size="sm" variant="ghost" disabled={page * 50 >= results.total} onClick={() => runSearch(page+1)}>Próx. ▶</Button>
                </div>
              </div>

              {/* Mass Edit */}
              {effectiveTargetsCount > 0 && (
                <div className="mt-4 rounded-lg border-2 border-[#065F46]/30 bg-emerald-50/40 p-3" data-testid="mass-edit">
                  <div className="flex items-center gap-1.5 mb-2">
                    <FileEdit className="h-3.5 w-3.5 text-[#065F46]" />
                    <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#065F46]">
                      Alterar {effectiveTargetsCount.toLocaleString("pt-BR")} colaborador(es)
                    </h3>
                  </div>
                  <UpdatesEditor
                    updates={updates}
                    onChange={setUpdates}
                    editableFields={editableFields}
                    fieldsByKey={fieldsByKey}
                    optionsBank={optionsBank}
                  />
                  <Button size="sm" onClick={doPreview} disabled={previewing || updates.length === 0}
                          className="mt-2 bg-[#065F46] hover:bg-[#054d38]" data-testid="btn-preview">
                    {previewing ? "Calculando..." : "Pré-visualizar"}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* Preview modal */}
      {preview && (
        <Dialog open onOpenChange={(v) => !v && setPreview(null)}>
          <DialogContent className="max-w-2xl" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#065F46]">
                <ShieldAlert className="h-5 w-5" /> Preview · Confirme antes de executar
              </DialogTitle>
              <DialogDescription>
                Serão aplicadas alterações nos colaboradores selecionados. Revise os totais abaixo.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2 text-[12px]">
                <Stat label="Encontrados" value={preview.found} />
                <Stat label="Selecionados" value={preview.sent_ids} />
                <Stat label="Fora do escopo" value={preview.out_of_scope} tone={preview.out_of_scope > 0 ? "bad" : "muted"} />
              </div>
              {preview.out_of_scope > 0 && (
                <div className="rounded-md bg-rose-50 border border-rose-200 p-2 text-[11.5px] text-rose-800">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  <b>{preview.out_of_scope}</b> IDs enviados estão fora do seu escopo multi-tenant e serão IGNORADOS.
                </div>
              )}
              {(preview.dependency_conflicts || []).length > 0 && (
                <div className="rounded-md bg-rose-50 border-2 border-rose-300 p-2 text-[11.5px] text-rose-900"
                     data-testid="dep-conflicts">
                  <div className="font-semibold flex items-center gap-1 mb-1">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    {preview.dependency_conflicts.length} inconsistência(s) de dependência detectadas — a operação será BLOQUEADA
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-0.5">
                    {preview.dependency_conflicts.slice(0, 8).map((c, i) => (
                      <div key={i} className="text-[10.5px]">
                        • {c.matricula || c.employee_id.slice(0, 8)} ({c.nome || "—"}): <b>{c.reason}</b> — {c.detail}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {preview.impact?.requires_double_confirmation && (
                <div className="rounded-md bg-amber-50 border-2 border-amber-300 p-2.5 text-[11.5px] text-amber-950"
                     data-testid="impact-banner">
                  <div className="font-semibold flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Alerta de impacto — requer confirmação dupla
                  </div>
                  <ul className="text-[11px] leading-tight list-disc pl-4 mb-1.5 space-y-0.5">
                    {preview.impact.high_volume && (
                      <li>Volume alto: <b>{preview.impact.total_targets.toLocaleString("pt-BR")}</b> colaboradores (limiar: {preview.impact.high_volume_threshold}).</li>
                    )}
                    {preview.impact.structural && (
                      <li>Alteração <b>estrutural</b> (impacta lotação/quadro): {preview.impact.structural_fields.join(", ")}</li>
                    )}
                    {preview.impact.sensitive && (
                      <li>Campos <b>sensíveis</b>: {preview.impact.sensitive_fields.join(", ")}</li>
                    )}
                  </ul>
                  <label className="flex items-center gap-2 text-[11.5px] cursor-pointer" data-testid="impact-ack">
                    <input type="checkbox" checked={impactAck} onChange={(e) => setImpactAck(e.target.checked)} />
                    Estou ciente do impacto e assumo responsabilidade pela execução.
                  </label>
                </div>
              )}
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2 max-h-64 overflow-y-auto">
                {preview.per_field.map((f, i) => (
                  <div key={i} className="text-[12px] py-1 border-b border-slate-200 last:border-0">
                    <b>{f.label}</b> · modo <code className="text-[10.5px] bg-white px-1 rounded">{f.mode}</code>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      Elegíveis: <b>{f.eligible}</b> · Preservados: <b>{f.preserved}</b>
                      {f.mode !== "clear" && <> · Novo valor: <code className="text-[10.5px] bg-white px-1 rounded">{String(f.new_value ?? "—")}</code></>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-2 text-[12px] text-emerald-900">
                Para confirmar, digite:
                <div className="mt-1 font-mono text-[12px] bg-white border border-emerald-200 rounded px-2 py-1.5 select-all"
                     data-testid="confirm-expected">
                  CONFIRMAR ALTERAR {preview.found}
                </div>
              </div>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                     placeholder={"CONFIRMAR ALTERAR " + preview.found}
                     data-testid="confirm-input"
                     className={confirmMatches ? "border-emerald-400" : ""} />
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={downloadDryRunCsv} disabled={applying}
                      className="mr-auto" data-testid="btn-dry-run-csv">
                <FileEdit className="h-3.5 w-3.5 mr-1.5" /> Baixar CSV (simulação)
              </Button>
              <Button variant="ghost" onClick={() => setPreview(null)} disabled={applying}>Cancelar</Button>
              <Button className="bg-[#065F46] hover:bg-[#054d38] text-white"
                      disabled={
                        applying ||
                        !confirmMatches ||
                        (preview.dependency_conflicts || []).length > 0 ||
                        (preview.impact?.requires_double_confirmation && !impactAck)
                      }
                      onClick={doApply} data-testid="btn-apply">
                {applying ? "Executando..."
                  : (preview.dependency_conflicts || []).length > 0 ? "Bloqueado por conflitos"
                  : (preview.impact?.requires_double_confirmation && !impactAck) ? "Aguardando confirmação dupla"
                  : "Executar alteração"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---- Componentes auxiliares -----------------------------------------------
function PresetRow({ preset, onLoad, onDelete, onRunNow, onPatch }) {
  const [editing, setEditing] = useState(false);
  const [cron, setCron] = useState(preset.schedule_cron || "");
  const isShared = !!preset.shared;
  const scheduled = !!preset.schedule_cron;
  const lastRun = preset.last_run_at ? new Date(preset.last_run_at).toLocaleString("pt-BR") : null;
  return (
    <div className="px-2 py-1.5 text-[11.5px]" data-testid={`preset-row-${preset.id}`}>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-slate-800 truncate">{preset.name}</span>
            {isShared && (
              <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[9.5px] font-medium border border-indigo-200"
                    data-testid={`preset-badge-shared-${preset.id}`}>
                Compartilhado
              </span>
            )}
            {scheduled && (
              <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 text-[9.5px] font-mono border border-amber-200"
                    data-testid={`preset-badge-cron-${preset.id}`}>
                ⏱ {preset.schedule_cron}
              </span>
            )}
          </div>
          <div className="text-[10.5px] text-slate-500 truncate">
            {preset.description || `${(preset.updates || []).length} update(s)`} · por {preset.created_by_email || "—"}
            {lastRun && <> · Última execução: {lastRun} ({preset.last_run_status || "—"})</>}
          </div>
        </div>
        <label className="flex items-center gap-1 text-[10.5px] text-slate-700 cursor-pointer" title="Compartilhar com o time">
          <input type="checkbox" checked={isShared}
                 onChange={(e) => onPatch({ shared: e.target.checked })}
                 data-testid={`preset-toggle-shared-${preset.id}`} />
          Compart.
        </label>
        <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)} className="h-6 text-[10.5px]"
                data-testid={`preset-edit-cron-${preset.id}`}>
          {editing ? "Fechar" : "Cron"}
        </Button>
        <Button size="sm" variant="outline" onClick={onLoad} className="h-6 text-[10.5px]"
                data-testid={`preset-load-${preset.id}`}>
          Carregar
        </Button>
        <Button size="sm" onClick={onRunNow} className="h-6 text-[10.5px] bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid={`preset-run-${preset.id}`}>
          Executar agora
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} className="h-6 w-6 p-0 text-rose-500"
                data-testid={`preset-del-${preset.id}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      {editing && (
        <div className="mt-1.5 flex items-center gap-1.5 pl-1">
          <Input value={cron} onChange={(e) => setCron(e.target.value)}
                 placeholder='Ex.: "0 6 1 * *" — dia 1 às 06h UTC. Vazio = sem agendamento.'
                 className="h-6 text-[11px] font-mono w-[300px]"
                 data-testid={`preset-cron-input-${preset.id}`} />
          <Button size="sm" onClick={() => { onPatch({ schedule_cron: cron.trim() || null }); setEditing(false); }}
                  className="h-6 text-[10.5px] bg-slate-800 hover:bg-slate-900">
            Salvar cron
          </Button>
          <span className="text-[10px] text-slate-500">
            Cron dispara a cada 15 min; formato UTC: min hora dia mês dia_semana
          </span>
        </div>
      )}
    </div>
  );
}

function UpdatesEditor({ updates, onChange, editableFields, fieldsByKey = {}, optionsBank = {} }) {
  const addUpdate = () => onChange([...updates, { field: editableFields[0]?.key || "", mode: "overwrite", value: "" }]);
  const update = (i, u) => onChange(updates.map((x, idx) => idx === i ? u : x));
  const remove = (i) => onChange(updates.filter((_, idx) => idx !== i));

  // Se houver update de company_id, filtramos sectors pela empresa alvo
  const targetCompanyId = updates.find(x => x.field === "company_id" && x.mode !== "clear")?.value || null;

  const optionsFor = (fieldKey) => {
    const meta = fieldsByKey[fieldKey] || {};
    const src = meta.options_source;
    if (!src) return null;
    if (src === "companies") return optionsBank.companies || [];
    if (src === "sectors") {
      const all = optionsBank.sectors || [];
      return targetCompanyId ? all.filter(s => s.company_id === targetCompanyId) : all;
    }
    if (src.startsWith("static:")) {
      const key = src.split(":")[1];
      const STATIC = {
        forma_contratacao: [
          { id: "CLT", label: "CLT" }, { id: "PJ", label: "PJ" },
          { id: "Estagio", label: "Estágio" }, { id: "Aprendiz", label: "Aprendiz" },
          { id: "Temporario", label: "Temporário" }, { id: "Terceiro", label: "Terceiro" },
          { id: "Autonomo", label: "Autônomo" },
        ],
        status: [
          { id: "ativo", label: "Ativo" }, { id: "ferias", label: "Férias" },
          { id: "afastado", label: "Afastado" }, { id: "em_experiencia", label: "Em experiência" },
          { id: "desligado", label: "Desligado" }, { id: "transferido", label: "Transferido" },
        ],
      };
      return STATIC[key] || [];
    }
    return null;
  };

  return (
    <div className="space-y-1.5">
      {updates.map((u, i) => {
        const opts = u.mode !== "clear" ? optionsFor(u.field) : null;
        const isFkNeedingCompany = u.field === "sector_id" && !targetCompanyId;
        return (
        <div key={i} className="flex flex-wrap gap-1 items-start">
          <Select value={u.field} onValueChange={(v) => update(i, { ...u, field: v, value: "" })}>
            <SelectTrigger className="h-7 w-[180px] text-[11px]" data-testid={`upd-field-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{editableFields.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={u.mode} onValueChange={(v) => update(i, { ...u, mode: v })}>
            <SelectTrigger className="h-7 w-[190px] text-[11px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="overwrite">Sobrescrever valor atual</SelectItem>
              <SelectItem value="fill_empty">Preencher somente vazios</SelectItem>
              <SelectItem value="clear">Limpar campo (⚠)</SelectItem>
            </SelectContent>
          </Select>
          {u.mode !== "clear" && (
            opts ? (
              <div className="flex flex-col gap-0.5">
                <Select value={u.value || ""} onValueChange={(v) => update(i, { ...u, value: v })}>
                  <SelectTrigger className="h-7 w-[240px] text-[11px]" data-testid={`upd-value-${i}`}>
                    <SelectValue placeholder="— selecione —" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[260px]">
                    {opts.length === 0 ? (
                      <div className="px-3 py-2 text-[11px] text-slate-500">
                        {isFkNeedingCompany
                          ? "Adicione o campo Empresa acima para listar setores compatíveis."
                          : "Nenhuma opção disponível."}
                      </div>
                    ) : opts.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {u.field === "company_id" && (
                  <span className="text-[10px] text-amber-700">
                    Ao mudar Empresa, adicione também Setor (e Sub-setor/Posição, se aplicável) — senão a operação será bloqueada por dependência.
                  </span>
                )}
              </div>
            ) : (
              <Input value={u.value ?? ""} onChange={(e) => update(i, { ...u, value: e.target.value })}
                     placeholder="Novo valor" className="h-7 w-[240px] text-[11px]" data-testid={`upd-value-${i}`} />
            )
          )}
          <Button size="sm" variant="ghost" onClick={() => remove(i)} className="h-7 w-7 p-0 text-rose-500">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      );})}
      <Button size="sm" variant="outline" onClick={addUpdate} className="h-7 text-[11px]" data-testid="btn-add-update">
        <Plus className="h-3 w-3 mr-1" /> Adicionar campo para alterar
      </Button>
    </div>
  );
}

function Stat({ label, value, tone = "muted" }) {
  const tones = {
    muted: { wrap: "bg-slate-50 border-slate-200", val: "text-slate-800" },
    bad:   { wrap: "bg-rose-50 border-rose-200", val: "text-rose-700" },
    ok:    { wrap: "bg-emerald-50 border-emerald-200", val: "text-emerald-700" },
  };
  const s = tones[tone] || tones.muted;
  return (
    <div className={`rounded-md border ${s.wrap} p-2`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[20px] font-display font-bold tabular-nums leading-none ${s.val}`}>{(value ?? 0).toLocaleString("pt-BR")}</div>
    </div>
  );
}
